import React, { useState, useEffect } from "react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  savePendingAuthSession,
  getPendingAuthSession,
  clearPendingAuthSession,
} from "../../lib/authSession";
import { StateCitySelector } from "../../lib/indiaData";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Building2,
  UploadCloud,
  CheckCircle2,
  ShieldCheck,
  Loader2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  RefreshCw
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { checkPasswordStrength, PasswordRequirements } from "../AuthForm";

export const WholesaleForm = ({ onSuccess, onDoneClose }) => {
  const { user, setUser } = useAuth();

  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const urlCode = (urlParams?.get("code") || "").replace(/\D/g, "").slice(0, 6);
  const urlEmail = (urlParams?.get("email") || "").trim();
  const urlStep = urlParams?.get("step") || "";

  const savedSession = getPendingAuthSession();
  const hasSavedWholesale = savedSession?.type === "wholesale_register" && (savedSession?.step === "otp" || savedSession?.form);

  const [step, setStep] = useState(() => {
    if (hasSavedWholesale && savedSession?.step === "otp") return "otp";
    if (urlStep === "otp") return "otp";
    return "form";
  });
  const [loading, setLoading] = useState(false);
  const [cardUploading, setCardUploading] = useState(false);
  const [otpCode, setOtpCode] = useState(() => urlCode || "");
  const [otpChannels, setOtpChannels] = useState(() => savedSession?.otpChannels || { email: true, mobile: false });
  const [countdown, setCountdown] = useState(() => {
    if (hasSavedWholesale && savedSession?.savedAt) {
      const elapsed = Math.floor((Date.now() - savedSession.savedAt) / 1000);
      return Math.max(0, 30 - elapsed);
    }
    return 0;
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState(() => {
    if (hasSavedWholesale && savedSession?.form) {
      return { ...savedSession.form };
    }
    return {
      name: user?.name || "",
      business_name: user?.business_name || "",
      whatsapp: user?.whatsapp || "",
      email: (user?.email && !user.email.endsWith("@zaveri.local") ? user.email : "") || urlEmail || "",
      state: "Maharashtra",
      city: "Mumbai",
      city_state: "Mumbai, Maharashtra",
      gstin: "",
      business_type: "Retailer", // Retailer / Wholesaler
      business_card_url: "",
      password: "",
      confirm_password: "",
    };
  });

  // Sync urlCode if user returns via link
  useEffect(() => {
    if (urlCode && step === "otp") {
      setOtpCode(urlCode);
    }
  }, [urlCode, step]);

  // Resend Countdown Timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCardUploading(true);
    try {
      const data = new FormData();
      data.append("files", file);
      const res = await api.post("/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.files?.[0]?.url) {
        setForm((prev) => ({ ...prev, business_card_url: res.data.files[0].url }));
        toast.success("Business card uploaded successfully!");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCardUploading(false);
    }
  };

  const isValidIndianPhone = (num) => /^[6-9]\d{9}$/.test(num);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.business_name || !form.whatsapp || !form.email || !form.city_state) {
      toast.error("Please fill in all required business and contact details.");
      return;
    }
    const digits = form.whatsapp.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    const digits_10 = digits.slice(-10);
    if (!isValidIndianPhone(digits_10)) {
      toast.error("Please enter a valid Indian mobile number starting with 6, 7, 8, or 9.");
      return;
    }

    const cleanEmail = form.email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      toast.error("Please enter a valid business email address.");
      return;
    }

    const pwdStrength = checkPasswordStrength(form.password);
    if (!pwdStrength.isValid) {
      toast.error("Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special symbol (min. 6 chars).");
      return;
    }

    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match. Please check and re-enter.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        whatsapp: digits_10,
        email: cleanEmail,
        state: form.state,
        city: form.city,
        city_state: form.city_state,
        gstin: form.gstin.trim(),
        business_type: form.business_type,
        business_card_url: form.business_card_url,
        password: form.password,
        confirm_password: form.confirm_password,
      };
      const res = await api.post("/auth/wholesale-send-otp", payload);
      setStep("otp");
      setCountdown(30);
      const mobileSent = Boolean(res.data?.mobile_otp_sent || res.data?.whatsapp_sent || res.data?.sms_sent);
      const emailSent = Boolean(res.data?.email_sent ?? true);
      const channels = { email: emailSent, mobile: mobileSent };
      setOtpChannels(channels);

      savePendingAuthSession({
        type: "wholesale_register",
        step: "otp",
        form: payload,
        otpChannels: channels,
      });

      toast.success(res.data?.message || (mobileSent ? `Verification code sent to ${cleanEmail} and +91 ${digits_10}` : `Verification code sent to ${cleanEmail}`));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e?.preventDefault();
    if (!otpCode || otpCode.trim().length !== 6) {
      toast.error("Please enter the 6-digit verification code.");
      return;
    }

    setLoading(true);
    try {
      const digits_10 = form.whatsapp.replace(/\D/g, "").slice(-10);
      const cleanEmail = form.email.trim().toLowerCase();

      const regRes = await api.post("/auth/wholesale-verify-register", {
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        whatsapp: digits_10,
        email: cleanEmail,
        state: form.state,
        city: form.city,
        city_state: form.city_state,
        gstin: form.gstin.trim(),
        business_type: form.business_type,
        business_card_url: form.business_card_url || "",
        password: form.password,
        otp: otpCode.trim(),
      });

      clearPendingAuthSession();
      setUser(regRes.data);
      setStep("done");
      toast.success("Wholesale Business Account verified! Protected pricing unlocked.");
      if (onSuccess) onSuccess(regRes.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 w-full min-w-0" data-testid="wholesale-b2b-form">
      {step === "form" && (
        <form onSubmit={handleSendOtp} className="space-y-3 sm:space-y-3.5 w-full min-w-0" data-testid="wholesale-registration-form">
          {/* Row 1: Full Name & Business Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 w-full min-w-0">
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Full Name *</Label>
              <input
                required
                data-testid="wholesale-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Ramesh Shah"
                className="w-full max-w-full min-w-0 box-border h-10 px-3 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-[#B8860B] focus:bg-white"
              />
            </div>
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Shop / Business Name *</Label>
              <input
                required
                data-testid="wholesale-business-name-input"
                value={form.business_name}
                onChange={set("business_name")}
                placeholder="e.g. Shah Jewellers"
                className="w-full max-w-full min-w-0 box-border h-10 px-3 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-[#B8860B] focus:bg-white"
              />
            </div>
          </div>

          {/* Row 2: WhatsApp Number & Business Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 w-full min-w-0">
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">WhatsApp Number *</Label>
              <div className="relative flex items-center w-full min-w-0">
                <span className="absolute left-3 text-xs font-semibold text-stone-500 font-mono">+91</span>
                <input
                  required
                  type="tel"
                  maxLength={10}
                  data-testid="wholesale-whatsapp-input"
                  value={form.whatsapp}
                  onChange={set("whatsapp")}
                  placeholder="9876543210"
                  className="w-full max-w-full min-w-0 box-border h-10 pl-11 pr-3 rounded-lg bg-stone-50 border border-stone-300 text-sm font-mono focus:outline-none focus:border-[#B8860B] focus:bg-white"
                />
              </div>
            </div>

            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Business Email *</Label>
              <div className="relative flex items-center w-full min-w-0">
                <Mail className="w-4 h-4 text-stone-400 absolute left-3 pointer-events-none" />
                <input
                  required
                  type="email"
                  data-testid="wholesale-email-input"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="business@example.com"
                  className="w-full max-w-full min-w-0 box-border h-10 pl-9 pr-3 rounded-lg bg-stone-50 border border-stone-300 text-sm font-mono focus:outline-none focus:border-[#B8860B] focus:bg-white"
                />
              </div>
            </div>
          </div>

          {/* Row 3: State & City Selector */}
          <StateCitySelector
            state={form.state}
            city={form.city}
            onStateChange={(s) => setForm((prev) => ({ ...prev, state: s, city_state: `${prev.city || ""}, ${s}` }))}
            onCityChange={(c) => setForm((prev) => ({ ...prev, city: c, city_state: `${c}, ${prev.state || ""}` }))}
            stateLabel="Business State *"
            cityLabel="Business City *"
            testIdPrefix="wholesale"
          />

          {/* Row 4: Business Type & GST */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 w-full min-w-0">
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Business Type *</Label>
              <select
                value={form.business_type}
                onChange={set("business_type")}
                className="w-full max-w-full min-w-0 box-border truncate h-10 px-2.5 sm:px-3 rounded-lg bg-stone-50 border border-stone-300 text-xs sm:text-sm focus:outline-none focus:border-[#B8860B] focus:bg-white"
              >
                <option value="Retailer">Retail Store / Jeweller</option>
                <option value="Wholesaler">Reseller / Semi-Wholesaler</option>
                <option value="Online Seller">Online Boutique / Instagram Seller</option>
              </select>
            </div>
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">GST Number (Optional)</Label>
              <input
                data-testid="wholesale-gstin-input"
                value={form.gstin}
                onChange={set("gstin")}
                placeholder="27AABCU9603R1ZM"
                className="w-full max-w-full min-w-0 box-border h-10 px-3 rounded-lg bg-stone-50 border border-stone-300 text-sm uppercase focus:outline-none focus:border-[#B8860B] focus:bg-white"
              />
            </div>
          </div>

          {/* Row 5: Password & Confirm Password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 w-full min-w-0">
            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Create Password *</Label>
              <div className="relative flex items-center w-full min-w-0">
                <Lock className="w-4 h-4 text-stone-400 absolute left-3 pointer-events-none" />
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  minLength={6}
                  data-testid="wholesale-password-input"
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Min. 6 characters"
                  className="w-full max-w-full min-w-0 box-border h-10 pl-9 pr-9 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-[#B8860B] focus:bg-white"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <div className="space-y-1 w-full min-w-0">
              <Label className="text-xs font-semibold text-stone-700 truncate">Confirm Password *</Label>
              <div className="relative flex items-center w-full min-w-0">
                <Lock className="w-4 h-4 text-stone-400 absolute left-3 pointer-events-none" />
                <input
                  required
                  type={showConfirmPassword ? "text" : "password"}
                  minLength={6}
                  data-testid="wholesale-confirm-password-input"
                  value={form.confirm_password}
                  onChange={set("confirm_password")}
                  placeholder="Re-enter password"
                  className="w-full max-w-full min-w-0 box-border h-10 pl-9 pr-9 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-[#B8860B] focus:bg-white"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2.5 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
                >
                  {showConfirmPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <PasswordRequirements password={form.password} />

          {/* Upload Business Card */}
          <div className="space-y-1.5 pt-1 w-full min-w-0">
            <Label className="text-xs font-semibold text-stone-700 flex items-center justify-between gap-1">
              <span className="truncate">Upload Visiting Card (Optional)</span>
              <span className="text-[10px] text-amber-700 font-normal shrink-0">Fast-tracks VIP</span>
            </Label>
            <div className="border-2 border-dashed border-stone-300 rounded-xl p-2.5 text-center bg-stone-50/70 hover:bg-stone-50 transition-colors relative w-full min-w-0 overflow-hidden box-border">
              {form.business_card_url ? (
                <div className="flex items-center justify-between gap-2 px-1 sm:px-2 w-full min-w-0">
                  <span className="text-xs text-emerald-700 font-medium flex items-center gap-1.5 min-w-0 truncate">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span className="truncate">Business Card Attached</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, business_card_url: "" }))}
                    className="text-xs text-red-500 hover:underline shrink-0 cursor-pointer"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-1">
                  <UploadCloud className="w-5 h-5 text-stone-400" />
                  <span className="text-xs text-stone-600 font-medium">Click to upload photo or PDF</span>
                  <span className="text-[10px] text-stone-400">JPG, PNG, WebP up to 10MB</span>
                  <input type="file" accept="image/*,.pdf" onChange={handleFileUpload} className="hidden" />
                </label>
              )}
              {cardUploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
                  <Loader2 className="w-5 h-5 animate-spin text-[#B8860B]" />
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || cardUploading}
            data-testid="wholesale-send-otp-btn"
            className="w-full h-11 bg-gradient-to-r from-[#996515] to-[#B8860B] hover:brightness-110 text-white font-semibold rounded-xl text-sm shadow-md cursor-pointer"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Continue & Send Verification Code
          </Button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={handleVerifyAndRegister} className="space-y-4 w-full min-w-0" data-testid="wholesale-otp-form">
          {/* Dual dispatch banner */}
          <div className="p-3.5 bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200/80 rounded-xl space-y-1.5 text-xs text-stone-800">
            <div className="flex items-center gap-2 font-bold text-[#0a2140]">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Wholesale Verification Code Sent!</span>
            </div>
            <p className="text-[11px] text-stone-600 leading-relaxed">
              {otpChannels.mobile
                ? "We have sent the same 6-digit verification code to:"
                : "We have sent the 6-digit verification code to your business email:"}
            </p>
            <div className="space-y-1 font-mono text-[11px] font-semibold text-stone-900 bg-white/70 p-2 rounded-lg border border-stone-200/60">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span className="truncate">{form.email}</span>
              </div>
              {otpChannels.mobile && (
                <div className="flex items-center gap-1.5">
                  <FaWhatsapp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>+91 {form.whatsapp.replace(/\D/g, "").slice(-10)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5 w-full min-w-0">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-stone-700">Enter 6-Digit OTP</Label>
              {countdown > 0 ? (
                <span className="text-[11px] text-stone-400 font-mono">Resend in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  className="text-[11px] text-[#B8860B] hover:underline font-bold inline-flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Resend Code</span>
                </button>
              )}
            </div>
            <input
              autoFocus
              required
              maxLength={6}
              data-testid="wholesale-otp-input"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="w-full max-w-full min-w-0 box-border h-12 text-center tracking-[0.25em] sm:tracking-[0.4em] font-mono text-lg sm:text-xl rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:border-[#B8860B] focus:bg-white font-bold"
            />
            <p className="text-[10px] text-stone-400 text-center">
              ⏱ Valid for 5 minutes. Enter the single OTP received on {otpChannels.mobile ? "Email or WhatsApp/SMS" : "your Email"}.
            </p>
          </div>

          <div className="flex gap-2 w-full min-w-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep("form");
                setOtpCode("");
                savePendingAuthSession({
                  type: "wholesale_register",
                  step: "form",
                  form,
                  otpChannels,
                });
              }}
              className="flex-1 h-11 rounded-xl cursor-pointer"
            >
              Edit Details
            </Button>
            <Button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              data-testid="wholesale-verify-btn"
              className="flex-1 h-11 bg-[#0a2140] hover:bg-[#183462] text-white font-semibold rounded-xl cursor-pointer"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Verify & Unlock Pricing
            </Button>
          </div>
        </form>
      )}

      {step === "done" && (
        <div className="text-center py-4 space-y-4">
          <div className="w-14 h-14 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-600 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div>
            <h3 className="font-serif-display text-lg font-bold text-stone-900">
              Wholesale Access Active!
            </h3>
            <p className="text-xs text-stone-600 max-w-sm mx-auto mt-1">
              Welcome, <span className="font-semibold text-stone-900">{form.business_name}</span>. You can now browse all direct-from-manufacturer products and make bulk WhatsApp inquiries.
            </p>
          </div>
          <Button
            type="button"
            onClick={onDoneClose}
            className="w-full h-11 bg-gradient-to-r from-[#996515] to-[#B8860B] text-white font-semibold rounded-xl cursor-pointer"
          >
            Browse Wholesale Catalogue
          </Button>
        </div>
      )}
    </div>
  );
};

export const WholesaleRegistrationModal = ({ open, onOpenChange, onSuccess }) => {
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg p-0 overflow-hidden bg-white border-0 shadow-2xl rounded-2xl sm:rounded-3xl max-h-[92vh] flex flex-col">
        <div className="bg-gradient-to-r from-[#1b150c] via-[#2a1e0c] to-[#120e08] text-white p-4 sm:p-6 border-b border-[#D4AF37]/30 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="p-1.5 rounded-lg bg-[#D4AF37]/20 text-[#E5C158] border border-[#D4AF37]/40">
              <Building2 className="w-4 h-4" />
            </span>
            <span className="text-[11px] font-semibold tracking-widest uppercase text-[#E5C158]">
              Wholesale Direct Access
            </span>
          </div>
          <DialogTitle className="font-serif-display text-xl sm:text-2xl font-bold text-white">
            B2B Business Registration
          </DialogTitle>
          <DialogDescription className="text-stone-300 text-xs mt-1">
            Get direct manufacturer pricing, volume tiers, and priority dispatch.
          </DialogDescription>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto overflow-x-hidden w-full min-w-0">
          <WholesaleForm onSuccess={onSuccess} onDoneClose={handleClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
